import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Checkbox,
  Divider,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconSparkles, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import type { DayInterval, OpeningHours, OpeningHoursResearchResult } from '../../../lib/api/domains/customerCare';
import { OPENING_STATUS_QUERY_KEY } from './openingStatusText';

const WEEKDAYS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const FULL_DAY: DayInterval[] = [{ from: '08:00', to: '17:00' }];
const WITH_LUNCH: DayInterval[] = [
  { from: '08:00', to: '12:00' },
  { from: '13:00', to: '17:00' },
];

function week(weekday: DayInterval[]): DayInterval[][] {
  return [weekday, weekday, weekday, weekday, weekday, [], []];
}

export const EMPTY_OPENING_HOURS: OpeningHours = {
  timeZone: 'Europe/Berlin',
  days: week(FULL_DAY),
  holidayRegion: 'DE',
  closures: [],
  note: '',
};

/** Clientseitig dieselben Prüfungen wie der Server — damit der Knopf nicht erst nach dem Klick widerspricht. */
function validate(hours: OpeningHours): string | null {
  for (let d = 0; d < 7; d++) {
    for (const i of hours.days[d]) {
      if (!/^\d{2}:\d{2}$/.test(i.from) || !/^\d{2}:\d{2}$/.test(i.to)) return `${WEEKDAYS[d]}: Uhrzeit fehlt`;
      if (i.to <= i.from) return `${WEEKDAYS[d]}: ${i.from}–${i.to} endet vor dem Beginn`;
    }
  }
  for (const c of hours.closures) {
    if (!c.from || !c.to) return 'Betriebsurlaub: Datum fehlt';
    if (c.to < c.from) return 'Betriebsurlaub endet vor dem Beginn';
  }
  return null;
}

/**
 * Der Dialog öffnet sich aus Abzeichen, die im Ticket-Cockpit (Drawer, zIndex 500+ und
 * Kopf-Popover +100), in der Kundeninfo (650) oder im Chat stehen — mit Mantines Standard
 * (200) lag er dahinter. Ein angeklickter Dialog gehört immer nach vorn.
 */
const OPENING_HOURS_MODAL_Z = 2000;

/**
 * Öffnungszeiten eines Kunden pflegen. Daraus zeigt der Support an Ticket, Chat und
 * Rückruf, ob beim Kunden gerade jemand da ist — nicht die Servicezeit, die steht in
 * der Fair-Use-Vereinbarung und treibt die SLA-Uhr.
 */
export function OpeningHoursModal({
  opened,
  onClose,
  customerId,
  customerName,
  value,
  queryKey,
  zIndex = OPENING_HOURS_MODAL_Z,
}: {
  opened: boolean;
  onClose: () => void;
  customerId: string;
  customerName: string;
  value: OpeningHours | null;
  /** Zusätzlich neu zu ladende Abfrage; Kundenakte und Abzeichen werden immer aufgefrischt. */
  queryKey?: readonly unknown[];
  zIndex?: number;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<OpeningHours>(value ?? EMPTY_OPENING_HOURS);
  const [research, setResearch] = useState<OpeningHoursResearchResult | null>(null);
  useEffect(() => {
    if (opened) {
      setDraft(value ?? EMPTY_OPENING_HOURS);
      setResearch(null);
    }
  }, [opened, value]);

  const researchMutation = useMutation({
    mutationFn: () => api.researchCustomerOpeningHours(customerId),
    onSuccess: (result) => {
      setResearch(result);
      const proposal = result.proposal;
      if (proposal) {
        // Betriebsurlaube findet die Suche nicht zuverlässig — eingetragene bleiben stehen.
        setDraft((d) => ({ ...proposal, closures: d.closures, note: proposal.note || d.note }));
      }
    },
    onError: (e: Error) => notifications.show({ title: 'Recherche fehlgeschlagen', message: e.message, color: 'red' }),
  });

  const catalogQuery = useQuery({
    queryKey: ['fair-use-catalog'],
    queryFn: () => api.getFairUseCatalog(),
    staleTime: 10 * 60_000,
    enabled: opened,
  });

  const save = useMutation({
    mutationFn: (hours: OpeningHours | null) => api.updateCustomerOpeningHours(customerId, hours),
    onSuccess: (_res, hours) => {
      if (queryKey) queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['customer-care'] });
      queryClient.invalidateQueries({ queryKey: [OPENING_STATUS_QUERY_KEY] });
      notifications.show({
        title: hours ? 'Öffnungszeiten gespeichert' : 'Öffnungszeiten entfernt',
        message: '',
        color: 'green',
      });
      onClose();
    },
    onError: (e: Error) => notifications.show({ title: 'Fehler', message: e.message, color: 'red' }),
  });

  const setDay = (index: number, intervals: DayInterval[]) =>
    setDraft((d) => ({ ...d, days: d.days.map((day, i) => (i === index ? intervals : day)) }));
  const setInterval = (index: number, slot: number, patch: Partial<DayInterval>) =>
    setDay(index, draft.days[index].map((iv, i) => (i === slot ? { ...iv, ...patch } : iv)));
  const setClosure = (index: number, patch: Partial<OpeningHours['closures'][number]>) =>
    setDraft((d) => ({ ...d, closures: d.closures.map((c, i) => (i === index ? { ...c, ...patch } : c)) }));

  const error = validate(draft);
  const today = new Date().toISOString().slice(0, 10);

  return (
    <Modal opened={opened} onClose={onClose} title={`Öffnungszeiten – ${customerName}`} size="lg" zIndex={zIndex}>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
          <Text size="xs" c="dimmed">
            Wann beim Kunden jemand erreichbar ist. Der Support sieht daraus an Ticket, Chat und Rückruf
            „geöffnet bis …", „Pause bis …" oder „geschlossen, öffnet …".
          </Text>
          <Button
            size="compact-sm"
            variant="light"
            color="violet"
            leftSection={<IconSparkles size={14} />}
            loading={researchMutation.isPending}
            onClick={() => researchMutation.mutate()}
            style={{ flexShrink: 0 }}
          >
            Von KIAN recherchieren
          </Button>
        </Group>

        {researchMutation.isPending && (
          <Text size="xs" c="dimmed">
            KIAN sucht auf der Webseite des Kunden und im Google-Profil … das dauert bis zu einer halben Minute.
          </Text>
        )}

        {research && <ResearchNotice result={research} />}

        <div>
          <Group gap="xs" mb={6} wrap="wrap">
            <Text size="xs" tt="uppercase" fw={600} c="dimmed" mr="auto">
              Wochenplan
            </Text>
            <Button size="compact-xs" variant="subtle" onClick={() => setDraft((d) => ({ ...d, days: week(FULL_DAY) }))}>
              Mo–Fr 08–17
            </Button>
            <Button size="compact-xs" variant="subtle" onClick={() => setDraft((d) => ({ ...d, days: week(WITH_LUNCH) }))}>
              Mo–Fr mit Mittagspause
            </Button>
            <Button
              size="compact-xs"
              variant="subtle"
              title="Die Zeiten vom Montag auf Dienstag bis Freitag übertragen"
              onClick={() =>
                setDraft((d) => ({ ...d, days: d.days.map((day, i) => (i >= 1 && i <= 4 ? d.days[0] : day)) }))
              }
            >
              Montag → Di–Fr
            </Button>
          </Group>

          <Stack gap={6}>
            {WEEKDAYS.map((label, index) => {
              const intervals = draft.days[index] ?? [];
              const open = intervals.length > 0;
              return (
                <Group key={label} gap="xs" wrap="wrap" align="center">
                  <Checkbox
                    size="xs"
                    w={100}
                    label={label}
                    checked={open}
                    onChange={(e) => {
                      const checked = e.currentTarget.checked;
                      setDay(index, checked ? FULL_DAY : []);
                    }}
                  />
                  {open ? (
                    <>
                      {intervals.map((interval, slot) => (
                        <Group key={slot} gap={4} wrap="nowrap">
                          {slot > 0 && (
                            <Text size="xs" c="dimmed" mr={2}>
                              +
                            </Text>
                          )}
                          <TimeInput
                            size="xs"
                            w={90}
                            aria-label={`${label} von`}
                            value={interval.from}
                            onChange={(e) => {
                              const v = e.currentTarget.value;
                              setInterval(index, slot, { from: v });
                            }}
                          />
                          <Text size="xs" c="dimmed">
                            –
                          </Text>
                          <TimeInput
                            size="xs"
                            w={90}
                            aria-label={`${label} bis`}
                            value={interval.to}
                            onChange={(e) => {
                              const v = e.currentTarget.value;
                              setInterval(index, slot, { to: v });
                            }}
                          />
                          {intervals.length > 1 && (
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              color="red"
                              aria-label="Zeitfenster entfernen"
                              onClick={() => setDay(index, intervals.filter((_, i) => i !== slot))}
                            >
                              <IconTrash size={12} />
                            </ActionIcon>
                          )}
                        </Group>
                      ))}
                      {intervals.length < 3 && (
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          title="Zweites Zeitfenster, z. B. nach der Mittagspause"
                          onClick={() => {
                            const last = intervals[intervals.length - 1];
                            const from = last && last.to < '23:00' ? last.to : '13:00';
                            setDay(index, [...intervals, { from, to: from < '17:00' ? '17:00' : '24:00' }]);
                          }}
                        >
                          + Zeitfenster
                        </Button>
                      )}
                    </>
                  ) : (
                    <Text size="xs" c="dimmed">
                      geschlossen
                    </Text>
                  )}
                </Group>
              );
            })}
          </Stack>

          <Select
            mt="sm"
            size="xs"
            w={260}
            label="Gesetzliche Feiertage"
            comboboxProps={{ withinPortal: true, zIndex: zIndex + 1 }}
            description="an diesen Tagen gilt der Kunde als geschlossen"
            allowDeselect={false}
            value={draft.holidayRegion}
            data={
              catalogQuery.data?.holidayRegions?.length
                ? catalogQuery.data.holidayRegions
                : [{ value: draft.holidayRegion, label: draft.holidayRegion }]
            }
            onChange={(v) => v && setDraft((d) => ({ ...d, holidayRegion: v }))}
          />
        </div>

        <Divider />

        <div>
          <Group justify="space-between" mb={6}>
            <Text size="xs" tt="uppercase" fw={600} c="dimmed">
              Betriebsurlaub / Schließtage
            </Text>
            <Button
              size="compact-xs"
              variant="subtle"
              leftSection={<IconPlus size={12} />}
              onClick={() =>
                setDraft((d) => ({ ...d, closures: [...d.closures, { from: today, to: today, label: '' }] }))
              }
            >
              Zeitraum
            </Button>
          </Group>
          {draft.closures.length === 0 ? (
            <Text size="xs" c="dimmed">
              Keine eingetragen.
            </Text>
          ) : (
            <Stack gap={6}>
              {draft.closures.map((c, index) => (
                <Group key={index} gap="xs" wrap="wrap" align="flex-end">
                  <TextInput
                    size="xs"
                    type="date"
                    label={index === 0 ? 'von' : undefined}
                    w={140}
                    value={c.from}
                    onChange={(e) => {
                      const v = e.currentTarget.value;
                      setClosure(index, { from: v, ...(c.to < v ? { to: v } : {}) });
                    }}
                  />
                  <TextInput
                    size="xs"
                    type="date"
                    label={index === 0 ? 'bis' : undefined}
                    w={140}
                    value={c.to}
                    onChange={(e) => {
                      const v = e.currentTarget.value;
                      setClosure(index, { to: v });
                    }}
                  />
                  <TextInput
                    size="xs"
                    label={index === 0 ? 'Bezeichnung' : undefined}
                    placeholder="z. B. Betriebsferien"
                    style={{ flex: 1, minWidth: 140 }}
                    maxLength={120}
                    value={c.label}
                    onChange={(e) => {
                      const v = e.currentTarget.value;
                      setClosure(index, { label: v });
                    }}
                  />
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    aria-label="Zeitraum entfernen"
                    onClick={() => setDraft((d) => ({ ...d, closures: d.closures.filter((_, i) => i !== index) }))}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
          )}
        </div>

        <Textarea
          size="xs"
          label="Hinweis"
          placeholder={'z. B. „Notfälle außerhalb: Handy Dr. Meier", „Mi-Nachmittag nur Labor besetzt"'}
          autosize
          minRows={2}
          maxRows={5}
          maxLength={1000}
          value={draft.note}
          onChange={(e) => {
            const v = e.currentTarget.value;
            setDraft((d) => ({ ...d, note: v }));
          }}
        />

        {error && (
          <Text size="xs" c="red">
            {error}
          </Text>
        )}

        <Group justify="space-between">
          {value ? (
            <Button
              variant="subtle"
              color="red"
              size="compact-sm"
              loading={save.isPending}
              onClick={() => save.mutate(null)}
            >
              Öffnungszeiten entfernen
            </Button>
          ) : (
            <span />
          )}
          <Group gap="xs">
            <Button variant="subtle" size="compact-sm" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              size="compact-sm"
              loading={save.isPending}
              disabled={!!error}
              onClick={() => save.mutate(draft)}
            >
              Speichern
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

const CONFIDENCE_LABEL: Record<string, { label: string; color: string }> = {
  high: { label: 'sicher', color: 'green' },
  medium: { label: 'wahrscheinlich', color: 'yellow' },
  low: { label: 'unsicher', color: 'orange' },
};

/** Nur http(s) als Link — die Adressen stammen aus der Websuche, nicht von uns. */
function safeHref(source: string): string | null {
  try {
    const url = new URL(source);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : null;
  } catch {
    return null;
  }
}

function ResearchNotice({ result }: { result: OpeningHoursResearchResult }) {
  if (!result.found) {
    return (
      <Alert color={result.available ? 'yellow' : 'gray'} variant="light" icon={<IconSparkles size={16} />}>
        <Text size="xs">{result.remark || 'KIAN hat keine Öffnungszeiten gefunden.'} Bitte von Hand eintragen.</Text>
      </Alert>
    );
  }
  const confidence = result.confidence ? CONFIDENCE_LABEL[result.confidence] : null;
  return (
    <Alert color="violet" variant="light" icon={<IconSparkles size={16} />}>
      <Stack gap={4}>
        <Group gap="xs">
          <Text size="xs" fw={600}>
            Vorschlag von KIAN übernommen — bitte prüfen, bevor du speicherst.
          </Text>
          {confidence && (
            <Badge size="xs" variant="light" color={confidence.color}>
              {confidence.label}
            </Badge>
          )}
        </Group>
        {result.remark && <Text size="xs">{result.remark}</Text>}
        {result.sources.length > 0 && (
          <Text size="xs" c="dimmed">
            Quellen:{' '}
            {result.sources.map((source, i) => {
              const href = safeHref(source);
              return (
                <span key={source}>
                  {i > 0 && ', '}
                  {href ? (
                    <Anchor href={href} target="_blank" rel="noopener noreferrer nofollow" size="xs">
                      {new URL(href).hostname}
                    </Anchor>
                  ) : (
                    source
                  )}
                </span>
              );
            })}
          </Text>
        )}
      </Stack>
    </Alert>
  );
}
